import { SensorConversions } from "../conversionFunctions/sensorConversions.js";
import { sensorParameterNumericValidators } from "../SensorConfigurationParameterHelpers.js";
import { SensorParameterConfigBase, SensorParameterValueType } from "../SensorConfigurationTypes.js";
import { SensorElementConfig } from "./types.js";

const liquidTankLevelParams = {
    slopeMetresPerVolt: {
        key: "slopeMetresPerVolt",
        type: SensorParameterValueType.NUMBER,
        name: "Slope (m/V)",
        description: "Tank level gain: how many metres of head the output increases per volt of input signal.",
        defaultValue: 1,
        validator: sensorParameterNumericValidators.isNumber,
        mustBeNonNegative: false,
        mustBeInteger: false,
        parameterUnitCategory: "liquidTankLevelSlope",
    },
    offsetMetres: {
        key: "offsetMetres",
        type: SensorParameterValueType.NUMBER,
        name: "Offset (m)",
        description:
            "Tank level offset added to slope × voltage. Typically a negative value that zeros the output at the sensor's minimum voltage.",
        defaultValue: 0,
        validator: sensorParameterNumericValidators.isNumber,
        mustBeNonNegative: false,
        mustBeInteger: false,
        parameterUnitCategory: "depth",
    },
} as const satisfies Record<string, SensorParameterConfigBase<number>>;

export const liquidTankLevelElementConfig: SensorElementConfig<
    typeof liquidTankLevelParams,
    typeof SensorConversions.linearWithMinAndMax
> = {
    legalConvertedRange: [0, null],
    conversionKey: "linearWithMinAndMax",
    parameterConfigs: liquidTankLevelParams,
    buildConversionParams: ({ slopeMetresPerVolt, offsetMetres }) => ({
        slope: slopeMetresPerVolt,
        offset: offsetMetres,
        outputMin: 0,
        outputMax: Number.MAX_VALUE,
    }),
    decodedUnits: "volt",
    convertedUnits: "meter",
    unitCategory: "liquidTankLevel",
    testCases: [
        {
            // AST400-5: 5V = 5 PSI = 3.516248 m of head. slope = 3.516248/5, offset = 0.
            //   at 1 V: 0.7032496 * 1 + 0 = 0.7032496 m
            parameters: { slopeMetresPerVolt: 3.516248 / 5, offsetMetres: 0 },
            decodedValue: 1,
            convertedValue: 0.7032496,
        },
        {
            // TE Connectivity US331: slope=2.6365 m/V, offset=-1.31825.
            //   at 1 V: 2.6365 * 1 - 1.31825 = 1.31825 m
            parameters: { slopeMetresPerVolt: 2.6365, offsetMetres: -1.31825 },
            decodedValue: 1,
            convertedValue: 1.31825,
        },
        {
            // TE Connectivity US331 below its zeroing voltage: clamped to 0 by outputMin.
            //   2.6365 * 0.2 - 1.31825 = 0.5273 - 1.31825 = -0.79095 m -> clamped to 0
            parameters: { slopeMetresPerVolt: 2.6365, offsetMetres: -1.31825 },
            decodedValue: 0.2,
            convertedValue: 0,
            skipBackwardConversionCheck: true,
        },
    ],
} as const;
